import React from 'react';
import { Link } from 'react-router-dom';
import { ClinicApiKeyCard } from './ClinicApiKeyCard';
import { getErrorMessage } from '../utils/errorHelpers';
import {
  emailClinicInvite,
  rotateClinicInvite,
  rotateClinicApiKey,
  updateClinicMemberRole,
  updateClinicMemberStatus,
  updateClinicSettings,
  setClinicPassword,
  type ClinicDemoProfile,
} from '../api/client';

function roleLabel(role: string) {
  if (role === 'admin') return 'Admin';
  if (role === 'navigator') return 'Navigator';
  if (role === 'oncologist') return 'Oncologist';
  return 'Clinician';
}

function modeLabel(mode: string) {
  if (mode === 'referring') return 'Sending referrals';
  if (mode === 'receiving') return 'Receiving referrals';
  return 'Sending and receiving';
}

export function ClinicPracticeHome({
  me,
  analytics,
  isAdmin,
  busy,
  setBusy,
  setError,
  refresh,
  apiKeyOnce,
  setApiKeyOnce,
  inviteEmail,
  setInviteEmail,
  inviteNote,
  setInviteNote,
  newPassword,
  setNewPassword,
  demoProfiles,
  onSwitchDemoClinic,
  onLogout,
}: {
  me: any;
  analytics: any;
  isAdmin: boolean;
  busy: boolean;
  setBusy: (v: boolean) => void;
  setError: (v: string | null) => void;
  refresh: () => Promise<void>;
  apiKeyOnce: string | null;
  setApiKeyOnce: (v: string | null) => void;
  inviteEmail: string;
  setInviteEmail: (v: string) => void;
  inviteNote: string | null;
  setInviteNote: (v: string | null) => void;
  newPassword: string;
  setNewPassword: (v: string) => void;
  demoProfiles: ClinicDemoProfile[];
  onSwitchDemoClinic: (id: string) => void;
  onLogout: () => void;
}): React.ReactElement {
  const clinic = me.clinic;
  const mix = Object.keys(analytics?.vertical_mix || {});

  return (
    <>
      <div className="oa-ws-panel">
        <h2>This practice</h2>
        <p className="oa-ws-muted">
          {clinic.city}
          {clinic.state ? `, ${clinic.state}` : ''}
          {clinic.city || clinic.state ? ' · ' : ''}
          {modeLabel(clinic.mode || 'both')}
        </p>
        <div className="oa-ws-invite-box">
          <div>
            <span className="oa-ws-label">Invite code</span>
            <strong className="oa-ws-invite-code">{clinic.invite_code}</strong>
            {clinic.invite_expires_at ? (
              <p className="oa-ws-hint">
                Good until {new Date(clinic.invite_expires_at).toLocaleDateString()}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            className="oa-ws-btn oa-ws-btn-ghost"
            onClick={() => {
              navigator.clipboard?.writeText(clinic.invite_code);
              setInviteNote('Invite code copied.');
            }}
          >
            Copy code
          </button>
        </div>

        {isAdmin ? (
          <div style={{ marginTop: '1.1rem' }}>
            <label className="oa-ws-label">Invite a colleague</label>
            <p className="oa-ws-hint" style={{ marginBottom: '0.55rem' }}>
              They get an email with the join link. Same ByOnco login they already use is fine.
            </p>
            <div className="oa-ws-actions" style={{ alignItems: 'center' }}>
              <input
                className="oa-ws-input"
                style={{ maxWidth: 320 }}
                type="email"
                placeholder="colleague@clinic.org"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
              />
              <button
                type="button"
                className="oa-ws-btn oa-ws-btn-primary"
                disabled={busy || !inviteEmail.includes('@')}
                onClick={async () => {
                  setBusy(true);
                  setInviteNote(null);
                  try {
                    const r = await emailClinicInvite(inviteEmail.trim());
                    setInviteNote(r.message || `Invite sent to ${inviteEmail.trim()}`);
                    setInviteEmail('');
                  } catch (e: any) {
                    setError(getErrorMessage(e) || 'Could not send that invite just now.');
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                {busy ? 'Sending…' : 'Send invite'}
              </button>
              <button
                type="button"
                className="oa-ws-btn oa-ws-btn-ghost"
                disabled={busy}
                onClick={() => {
                  const url = `${window.location.origin}/oncoagent/app/clinic?invite=${encodeURIComponent(clinic.invite_code)}`;
                  navigator.clipboard?.writeText(url);
                  setInviteNote('Join link copied. Paste it in a text if you prefer.');
                }}
              >
                Copy join link
              </button>
              <button
                type="button"
                className="oa-ws-btn oa-ws-btn-ghost"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    const r = await rotateClinicInvite();
                    await refresh();
                    navigator.clipboard?.writeText(r.invite_code);
                    setInviteNote('New invite code is ready and copied.');
                  } catch (e: any) {
                    setError(getErrorMessage(e) || 'Could not refresh the invite code.');
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                New invite code
              </button>
            </div>
            {inviteNote ? <p className="oa-ws-ok">{inviteNote}</p> : null}
          </div>
        ) : null}

        <div className="oa-ws-actions">
          <Link className="oa-ws-btn oa-ws-btn-primary" to="/oncoagent/app/inbox">
            Open inbox
          </Link>
          <Link className="oa-ws-btn oa-ws-btn-secondary" to="/oncoagent/app/refer">
            Start a referral
          </Link>
          {demoProfiles.length > 1 ? (
            <select
              className="oa-ws-select"
              style={{ maxWidth: 260 }}
              value={clinic.clinic_id}
              onChange={(e) => onSwitchDemoClinic(e.target.value)}
              aria-label="Switch practice"
            >
              {demoProfiles.map((p) => (
                <option key={p.clinic_id} value={p.clinic_id}>
                  {p.clinic_name || 'Practice'}
                </option>
              ))}
            </select>
          ) : null}
          <button type="button" className="oa-ws-btn oa-ws-btn-ghost" onClick={onLogout}>
            Leave this practice
          </button>
        </div>

        {!me.member?.password_set ? (
          <div style={{ marginTop: '1.15rem' }}>
            <label className="oa-ws-label">Set a password for next time</label>
            <div className="oa-ws-actions">
              <input
                className="oa-ws-input"
                type="password"
                placeholder="8+ characters, mixed case, a number"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <button
                type="button"
                className="oa-ws-btn oa-ws-btn-secondary"
                disabled={busy || newPassword.length < 8}
                onClick={async () => {
                  setBusy(true);
                  try {
                    await setClinicPassword(newPassword);
                    setNewPassword('');
                    await refresh();
                  } catch (e: any) {
                    setError(getErrorMessage(e) || 'Could not save that password.');
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Save password
              </button>
            </div>
          </div>
        ) : (
          <p className="oa-ws-hint" style={{ marginTop: '0.9rem' }}>
            Signed in as {me.member?.email}
          </p>
        )}
      </div>

      {isAdmin ? (
        <div className="oa-ws-panel">
          <ClinicApiKeyCard
            prefix={clinic.api_key_prefix}
            secret={apiKeyOnce}
            busy={busy}
            onDismissSecret={() => setApiKeyOnce(null)}
            onCreateNew={async () => {
              setBusy(true);
              try {
                const r = await rotateClinicApiKey();
                setApiKeyOnce(r.api_key);
                await refresh();
              } catch (e: any) {
                setError(getErrorMessage(e) || 'Could not create a new key.');
              } finally {
                setBusy(false);
              }
            }}
          />
        </div>
      ) : null}

      {analytics ? (
        <div className="oa-ws-panel">
          <h2>Last 30 days</h2>
          <div className="oa-ws-stats">
            <div>
              <strong>{analytics.inbound_count ?? 0}</strong>
              <span>Received</span>
            </div>
            <div>
              <strong>{analytics.outbound_count ?? 0}</strong>
              <span>Sent</span>
            </div>
            <div>
              <strong>{analytics.open_inbound ?? 0}</strong>
              <span>Still open</span>
            </div>
            <div>
              <strong>
                {analytics.avg_ack_hours != null ? `${analytics.avg_ack_hours}h` : 'n/a'}
              </strong>
              <span>Time to acknowledge</span>
            </div>
          </div>
          {mix.length ? (
            <p className="oa-ws-hint" style={{ marginTop: '0.85rem' }}>
              Mix: {mix.join(', ')}
            </p>
          ) : (
            <p className="oa-ws-hint" style={{ marginTop: '0.85rem' }}>
              No referrals in this window yet. Start one from Refer, or wait for a colleague to send
              you a packet.
            </p>
          )}
        </div>
      ) : null}

      <div className="oa-ws-grid cols-2">
        <div className="oa-ws-panel">
          <h3>People here</h3>
          <ul className="oa-ws-list">
            {(me.members || []).map((m: any) => (
              <li key={m.member_id}>
                <strong>{m.name}</strong>
                <div className="oa-ws-muted">
                  {m.email} · {roleLabel(m.role)}
                  {m.status === 'disabled' ? ' · paused' : ''}
                </div>
                {isAdmin && m.member_id !== me.member?.member_id ? (
                  <div className="oa-ws-actions" style={{ marginTop: 8 }}>
                    <select
                      className="oa-ws-select"
                      value={m.role}
                      onChange={async (e) => {
                        try {
                          await updateClinicMemberRole(m.member_id, e.target.value);
                          await refresh();
                        } catch (err: any) {
                          setError(getErrorMessage(err) || 'Could not update that role.');
                        }
                      }}
                    >
                      <option value="clinician">Clinician</option>
                      <option value="navigator">Navigator</option>
                      <option value="oncologist">Oncologist</option>
                      <option value="admin">Admin</option>
                    </select>
                    <button
                      type="button"
                      className="oa-ws-btn oa-ws-btn-ghost"
                      onClick={async () => {
                        try {
                          await updateClinicMemberStatus(
                            m.member_id,
                            m.status === 'disabled' ? 'active' : 'disabled'
                          );
                          await refresh();
                        } catch (err: any) {
                          setError(getErrorMessage(err) || 'Could not update that person.');
                        }
                      }}
                    >
                      {m.status === 'disabled' ? 'Restore' : 'Pause'}
                    </button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        </div>

        {isAdmin ? (
          <div className="oa-ws-panel">
            <h3>How this desk works</h3>
            <label className="oa-ws-label">Referrals</label>
            <select
              className="oa-ws-select"
              value={clinic.mode || 'both'}
              onChange={async (e) => {
                try {
                  await updateClinicSettings({ mode: e.target.value as any });
                  await refresh();
                } catch (err: any) {
                  setError(getErrorMessage(err) || 'Could not save that setting.');
                }
              }}
            >
              <option value="both">Send and receive</option>
              <option value="referring">Send only</option>
              <option value="receiving">Receive only</option>
            </select>
            <div style={{ height: 14 }} />
            <label className="oa-ws-label">Shared group name (optional)</label>
            <input
              className="oa-ws-input"
              defaultValue={clinic.network_id || ''}
              placeholder="If several clinics share patients"
              onBlur={async (e) => {
                try {
                  await updateClinicSettings({ network_id: e.target.value });
                  await refresh();
                } catch (err: any) {
                  setError(getErrorMessage(err) || 'Could not save the group name.');
                }
              }}
            />
            <p className="oa-ws-hint">
              Packets print as PDF or download as FHIR for the receiving EHR. Email the packet or
              the join link. No extra vendor account required.
            </p>
          </div>
        ) : (
          <div className="oa-ws-panel">
            <h3>Packets</h3>
            <p className="oa-ws-muted">
              From a referral you can print a packet or download FHIR for the receiving system.
            </p>
          </div>
        )}
      </div>
    </>
  );
}
